import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildAppearanceCss, googleFontsUrl, type Appearance } from "@/lib/appearance";

type Ctx = { appearance: Appearance; loading: boolean };
const AppearanceCtx = createContext<Ctx>({ appearance: {}, loading: true });

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ["appearance"],
    queryFn: async (): Promise<Appearance> => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "appearance")
        .maybeSingle();
      if (error) return {};
      return (data?.value as Appearance) ?? {};
    },
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const appearance = data ?? {};

  // Inject CSS only when non-empty overrides exist to avoid forced reflows
  useEffect(() => {
    if (typeof document === "undefined" || Object.keys(appearance).length === 0) return;
    const id = "appearance-overrides";
    let el = document.getElementById(id) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    const css = buildAppearanceCss(appearance);
    if (css && el.textContent !== css) {
      el.textContent = css;
    }
  }, [appearance]);

  // Inject Google Fonts link
  useEffect(() => {
    if (typeof document === "undefined") return;
    const id = "appearance-fonts";
    const href = googleFontsUrl(appearance);
    let el = document.getElementById(id) as HTMLLinkElement | null;
    if (!href) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement("link");
      el.id = id;
      el.rel = "stylesheet";
      document.head.appendChild(el);
    }
    if (el.href !== href) el.href = href;
  }, [appearance]);

  // Favicon
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!appearance.favicon_url) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = appearance.favicon_url;
  }, [appearance.favicon_url]);

  // Default theme application (one-time on mount based on user override)
  useEffect(() => {
    if (typeof document === "undefined") return;
    const stored = localStorage.getItem("theme");
    const userOverride = appearance.allow_user_override !== false;
    if (userOverride && stored) return; // respect user choice
    const def = appearance.default_theme;
    if (!def) return;
    const isDark =
      def === "dark" ||
      (def === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", isDark);
  }, [appearance.default_theme, appearance.allow_user_override]);

  const value = useMemo(() => ({ appearance, loading: isLoading }), [appearance, isLoading]);
  return <AppearanceCtx.Provider value={value}>{children}</AppearanceCtx.Provider>;
}

export const useAppearance = () => useContext(AppearanceCtx);

/** SSR-safe hook returning whether we're mounted on the client. */
export function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
}
